/** Listening-practice audio transport for Hören items.
 *
 * The audio is EITHER a real recording (`src`, a generated MP3) or, until
 * one exists, the browser's German TTS reading the transcript (`text`).
 *
 * There is deliberately NO "show transcript" control here. Reading the
 * script during a listening exercise defeats it — the transcript is
 * answer-adjacent and is shown only AFTER answering, in the result view.
 * When a recording exists the server does not even send the transcript to
 * the page; `text` is present only in the TTS-fallback case, and even then
 * it is used to make a sound, never displayed.
 */
import { useEffect, useState } from "react";
import { useT } from "../../i18n";
import { cancelSpeech, pauseSpeech, resumeSpeech, speak, ttsAvailable } from "../../utils/tts";

type Status = "idle" | "playing" | "paused";

const RATES = [0.75, 1, 1.25];

export function AudioPlayer({ text, src }: { text?: string; src?: string }) {
  const t = useT();
  const available = ttsAvailable();
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState(0);
  const [rate, setRate] = useState(1);

  // Stop any speech when the learner leaves the item.
  useEffect(() => () => cancelSpeech(), []);

  const start = (r: number) => {
    if (!text) return;
    setProgress(0);
    speak(text, {
      rate: r,
      onEnd: () => {
        setStatus("idle");
        setProgress(1);
      },
      onBoundary: setProgress,
    });
    setStatus("playing");
  };

  const onToggle = () => {
    if (status === "playing") {
      pauseSpeech();
      setStatus("paused");
    } else if (status === "paused") {
      resumeSpeech();
      setStatus("playing");
    } else {
      start(rate);
    }
  };

  const onRestart = () => {
    cancelSpeech();
    start(rate);
  };

  const onRate = (r: number) => {
    setRate(r);
    if (status !== "idle") {
      cancelSpeech();
      start(r); // speechSynthesis can't change rate mid-utterance — restart
    }
  };

  return (
    <div className="rounded-control border border-line bg-canvas p-3">
      {src ? (
        <audio
          data-testid="native-audio"
          controls
          preload="none"
          src={src}
          className="w-full"
        />
      ) : available && text ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={onToggle}
              aria-label={status === "playing" ? t("player.pause") : t("player.listen")}
              className="rounded-control bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark"
            >
              <span aria-hidden>{status === "playing" ? "⏸" : "▶"}</span>{" "}
              {status === "playing" ? t("player.pause") : t("player.play")}
            </button>
            <button
              onClick={onRestart}
              aria-label={t("player.restart")}
              className="text-sm font-semibold text-muted hover:text-brand"
            >
              <span aria-hidden>↺</span> {t("player.restart")}
            </button>
            <div className="ml-auto flex items-center gap-1">
              {RATES.map((r) => (
                <button
                  key={r}
                  onClick={() => onRate(r)}
                  aria-pressed={rate === r}
                  className={`rounded-control px-2 py-1 text-xs font-semibold ${
                    rate === r ? "bg-brand-soft text-brand-dark" : "text-muted hover:text-brand"
                  }`}
                >
                  {r}×
                </button>
              ))}
            </div>
          </div>
          <div
            className="mt-3 h-1.5 overflow-hidden rounded-full bg-line"
            role="progressbar"
            aria-label={t("player.listen")}
            aria-valuenow={Math.round(progress * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded-full bg-brand transition-[width] duration-200"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        </>
      ) : (
        <p className="text-sm text-muted">{t("player.audioUnavailable")}</p>
      )}
    </div>
  );
}
